/**
 * Cursor-following readouts, shared by the request matrix and the uptime
 * wall.
 *
 * Both used to pin their popover to one axis — a fixed top, or the centre
 * of the hovered column — which reads as detached the moment the pointer is
 * anywhere else in the band. A readout that tracks the cursor stays where
 * the eye already is.
 *
 * The container's geometry comes from the mousemove event's own
 * `currentTarget` — the handler is attached to the container, so no ref is
 * needed. Reading a ref during render is a lint error and a stale-value bug
 * waiting to happen; not having one is better than working around it.
 */

import { useCallback, useState, type CSSProperties, type MouseEvent } from 'react'

/** Gap between the pointer and the readout's near corner. */
const CURSOR_GAP = 14

export interface CursorAnchor {
  /** Pointer position relative to the container. */
  x: number
  y: number
  containerWidth: number
  containerHeight: number
}

interface CursorTracking {
  anchor: CursorAnchor | null
  /** Attach to the container the readout is positioned within. */
  track: (event: MouseEvent<HTMLElement>) => void
  clear: () => void
}

export function useCursorAnchor(): CursorTracking {
  const [anchor, setAnchor] = useState<CursorAnchor | null>(null)

  const track = useCallback((event: MouseEvent<HTMLElement>): void => {
    const base = event.currentTarget.getBoundingClientRect()
    setAnchor({
      x: event.clientX - base.left,
      y: event.clientY - base.top,
      containerWidth: base.width,
      containerHeight: base.height,
    })
  }, [])

  const clear = useCallback((): void => {
    setAnchor(null)
  }, [])

  return { anchor, track, clear }
}

/**
 * Place a readout of the given size beside the cursor, flipping to the
 * other side when it would leave the container rather than being clipped
 * or dragging the page wider.
 */
export function anchorStyle(
  anchor: CursorAnchor,
  width: number,
  height: number,
): CSSProperties {
  const flipX = anchor.x + CURSOR_GAP + width > anchor.containerWidth
  const flipY = anchor.y + CURSOR_GAP + height > anchor.containerHeight
  return {
    left: flipX ? anchor.x - CURSOR_GAP - width : anchor.x + CURSOR_GAP,
    // The uptime wall is shorter than its readout, so a flip there would
    // push it off the top; clamp at the container instead.
    top: flipY ? Math.max(0, anchor.y - CURSOR_GAP - height) : anchor.y + CURSOR_GAP,
    width,
  }
}
